import { useRef, useState } from "react";
import { api, apiError, uploadFile } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/cashbox/SignaturePad";
import { formatCZK } from "@/lib/format";
import { toast } from "sonner";

// Vypořádání případu podpisem. Částku ani druh pohybu klient neposílá —
// server si je spočítá z diff (jinak by šly podvrhnout).
export function SettleOverlay({ open, caseData, onClose, onDone }) {
  const padRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  if (!caseData) return null;
  const diff = caseData.diff || 0;
  const line = diff < -0.004
    ? { label: `Převezmete ${formatCZK(-diff)}`, sub: "vrací se zpět do pokladny" }
    : diff > 0.004
      ? { label: `Vyplatíte ${formatCZK(diff)}`, sub: "utratil víc, než dostal — firma doplácí" }
      : { label: "Bez pohybu hotovosti", sub: "doklady sedí na haléř" };

  const confirm = async () => {
    if (padRef.current?.isEmpty()) return;
    setBusy(true);
    try {
      const blob = await padRef.current.toBlob();
      const file = new File([blob], "podpis.png", { type: "image/png" });
      const signature_url = await uploadFile(file);
      await api.post(`/cashbox/cases/${caseData.id}/settle`, {
        signature_url, signed_name: caseData.person_name });
      toast.success(`${caseData.human_id} vypořádáno a uzavřeno.`);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="settle-overlay">
        <DialogHeader>
          <DialogTitle>Vypořádání {caseData.human_id}</DialogTitle>
          <DialogDescription className="sr-only">Potvrzení vypořádání podpisem</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-secondary/60 border border-border rounded-[14px] p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Vydáno</div>
              <div className="font-bold tabular-nums text-sm mt-0.5">{formatCZK(caseData.amount_issued)}</div>
            </div>
            <div className="bg-secondary/60 border border-border rounded-[14px] p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Doklady</div>
              <div className="font-bold tabular-nums text-sm mt-0.5">{formatCZK(caseData.documented)}</div>
            </div>
            <div className="bg-secondary/60 border border-border rounded-[14px] p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Rozdíl</div>
              <div className={`font-bold tabular-nums text-sm mt-0.5 ${diff < 0 ? "text-brand" : diff > 0 ? "text-destructive" : ""}`}>
                {formatCZK(diff)}
              </div>
            </div>
          </div>
          <div className="rounded-[14px] bg-brand/10 border border-brand/30 p-3">
            <div className="font-black text-brand" data-testid="settle-summary">{line.label}</div>
            <div className="text-[13px] text-muted-foreground mt-0.5">{line.sub} · podepisuje {caseData.person_name}</div>
          </div>
          <SignaturePad ref={padRef} onChange={setEmpty} />
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
              className="h-11 w-full md:w-auto" data-testid="settle-cancel">Zrušit</Button>
            <Button onClick={confirm} disabled={empty || busy}
              className="h-11 w-full md:w-auto font-bold" data-testid="settle-confirm">
              {busy ? "Ukládám…" : "Potvrdit vypořádání"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
